/**
 * Auto Sync Context
 *
 * Provides global state for background auto-sync operations.
 * This allows the Accounts page to show spinning sync buttons
 * when auto-sync is running in the background.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface AutoSyncContextValue {
  /** Whether an auto-sync is currently in progress */
  isAutoSyncing: boolean;
  /** Set of account IDs currently being synced */
  syncingAccountIds: Set<string>;
  /** Start auto-sync (sets syncing state) */
  startAutoSync: (accountIds: string[]) => void;
  /** End auto-sync (clears syncing state) */
  endAutoSync: () => void;
}

const AutoSyncContext = createContext<AutoSyncContextValue | undefined>(undefined);

interface AutoSyncProviderProps {
  children: ReactNode;
}

export function AutoSyncProvider({ children }: AutoSyncProviderProps) {
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const [syncingAccountIds, setSyncingAccountIds] = useState<Set<string>>(new Set());

  const startAutoSync = useCallback((accountIds: string[]) => {
    setIsAutoSyncing(true);
    setSyncingAccountIds(new Set(accountIds));
  }, []);

  const endAutoSync = useCallback(() => {
    setIsAutoSyncing(false);
    setSyncingAccountIds(new Set());
  }, []);

  const value = useMemo<AutoSyncContextValue>(
    () => ({ isAutoSyncing, syncingAccountIds, startAutoSync, endAutoSync }),
    [isAutoSyncing, syncingAccountIds, startAutoSync, endAutoSync]
  );

  return (
    <AutoSyncContext.Provider value={value}>
      {children}
    </AutoSyncContext.Provider>
  );
}

export function useAutoSyncContext(): AutoSyncContextValue {
  const context = useContext(AutoSyncContext);
  if (!context) {
    throw new Error('useAutoSyncContext must be used within an AutoSyncProvider');
  }
  return context;
}
